import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { Radar10, type SerieDoRadar } from '@/components/conselheiro/Radar10.js';
import { baixarBalancoPdf } from '@/lib/balanco-pdf.js';
import { MES_DO_BALANCO, NOME_DA_RECOMENDACAO, RECOMENDACOES, motivoParaNaoPublicar, type NumerosDoBalanco, type Recomendacao } from '@shared/balanco.js';

/**
 * O balanço do fim do ciclo (etapa 16). O Principal vê os números a partir
 * do 11º mês, escreve o parecer aos sócios e a recomendação à gestão, e
 * publica. Sócios veem depois de publicado, sem a recomendação; todos baixam
 * em PDF.
 */

type Numeros = Omit<NumerosDoBalanco, 'periodo' | 'indicadores'> & {
  periodo: { inicio: string; fim: string | null; mes: number };
  indicadores: (Omit<NumerosDoBalanco['indicadores'][number], 'primeiro' | 'ultimo'> & {
    primeiro: { competencia: string; valor: number };
    ultimo: { competencia: string; valor: number };
  })[];
};

type Resposta = {
  quem: 'CONSELHEIRO' | 'SOCIO' | 'RESPONSAVEL';
  principal: boolean;
  disponivel: boolean;
  ciclo: { id: string; inicio_em: string; fim_em: string | null; status: string } | null;
  pode_editar?: boolean;
  publicado_em?: string | null;
  parecer?: string | null;
  numeros?: Numeros;
  recomendacao?: Recomendacao | null;
  recomendacao_motivo?: string | null;
  renovacao?: { decidida: string | null; decidida_em: string | null } | null;
  publicados: { ciclo_id: string; inicio_em: string; publicado_em: string }[];
};

const data = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');
const n1 = (x: number | null) => (x == null ? '—' : x.toLocaleString('pt-BR', { maximumFractionDigits: 1 }));
const valor = (x: number | null, u: string | null) =>
  x == null ? '—' : `${x.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${u ? ` ${u}` : ''}`;
const mes = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const botao = 'rounded-element px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50';
const DECISAO: Record<string, string> = { RENOVAR: 'a gestão decidiu renovar', ENCERRAR: 'a gestão decidiu encerrar' };

export function BalancoDoCiclo({ empresaId, empresa, embutido = false }: { empresaId: string; empresa: string; embutido?: boolean }) {
  const qc = useQueryClient();
  const [cicloEscolhido, setCiclo] = useState<string | null>(null);
  const chave = ['conselheiro-balanco', empresaId, cicloEscolhido];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () =>
      (await api.get<Resposta>(`/conselheiro/empresas/${empresaId}/balanco`, { params: cicloEscolhido ? { ciclo: cicloEscolhido } : {} })).data,
  });
  const b = q.data;
  if (q.isLoading || !b || !b.disponivel || !b.numeros || !b.ciclo) return null;
  const n = b.numeros;

  const series: SerieDoRadar[] = [];
  const partida = n.radar.temas.map((t) => t.partida);
  const final = n.radar.temas.map((t) => t.final);
  if (partida.every((x) => x != null)) series.push({ rotulo: 'Partida', notas: partida as number[], cor: '#9ca3af', tracejada: true });
  if (final.every((x) => x != null))
    series.push({ rotulo: n.radar.final_tipo === 'MARCO' ? 'Último marco' : 'Final', notas: final as number[], cor: 'var(--primary)', preenchida: true });

  const outros = b.publicados.filter((p) => p.ciclo_id !== b.ciclo!.id);
  return (
    // Embutido no cartão do sócio: ocupa a largura toda e leva o rótulo do cartão; só existe depois de publicado.
    <section className={embutido ? 'md:col-span-2' : 'rounded-card border border-ha-border2 bg-ha-card p-5'}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          {embutido ? (
            <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">Balanço do ciclo</h3>
          ) : (
            <h2 className="text-base font-semibold text-ha-text">Balanço do ciclo</h2>
          )}
          <p className="mt-0.5 text-xs text-ha-muted">
            {data(b.ciclo.inicio_em)} a {data(b.ciclo.fim_em)} ·{' '}
            {b.publicado_em ? `publicado em ${data(b.publicado_em)}` : 'rascunho — os sócios ainda não veem'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {outros.length ? (
            <select
              className="rounded-element border border-ha-border2 bg-ha-card px-2 py-1 text-xs"
              value={cicloEscolhido ?? ''}
              onChange={(e) => setCiclo(e.target.value || null)}
              aria-label="Outro ciclo"
            >
              <option value="">Este ciclo</option>
              {outros.map((p) => (
                <option key={p.ciclo_id} value={p.ciclo_id}>
                  Ciclo de {data(p.inicio_em)}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="text-xs text-accent underline"
            onClick={() => baixarBalancoPdf({ empresa, publicado_em: b.publicado_em ?? null, parecer: b.parecer ?? null, numeros: n })}
          >
            Baixar PDF
          </button>
        </div>
      </div>

      {b.quem === 'CONSELHEIRO' && b.renovacao?.decidida ? (
        <p className="mb-4 rounded-element border border-ha-border2 bg-ha-bg px-3 py-2 text-xs text-ha-text">
          Renovação: {DECISAO[b.renovacao.decidida] ?? b.renovacao.decidida} em {data(b.renovacao.decidida_em)}. Acontece no fim do ciclo.
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <h3 className="mb-1 text-sm font-semibold text-ha-text">O radar</h3>
          <p className="mb-2 text-xs text-ha-muted">
            Média de {n1(n.radar.partida)} na partida e {n1(n.radar.final)} {n.radar.final_tipo === 'MARCO' ? 'no último marco' : 'no final'}.
            {n.radar.final_tipo === 'MARCO' ? ' Feche o radar final para o balanço ficar completo.' : ''}
            {!n.radar.final_tipo ? ' Ainda sem radar final.' : ''}
          </p>
          {series.length ? <Radar10 series={series} tamanho={360} /> : null}
        </div>
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-ha-muted">
              <tr>
                <th className="py-1 pr-2 font-medium">Tema</th>
                <th className="py-1 pr-2 font-medium">Partida</th>
                <th className="py-1 pr-2 font-medium">Agora</th>
                <th className="py-1 font-medium">Variação</th>
              </tr>
            </thead>
            <tbody>
              {n.radar.temas.map((t) => (
                <tr key={t.tema} className="border-t border-ha-border2">
                  <td className="py-1.5 pr-2 text-ha-text">{t.nome}</td>
                  <td className="py-1.5 pr-2">{n1(t.partida)}</td>
                  <td className="py-1.5 pr-2 font-semibold">{n1(t.final)}</td>
                  <td className={`py-1.5 font-semibold ${t.delta != null && t.delta < 0 ? 'text-red-700' : 'text-ha-text'}`}>
                    {t.delta == null ? '—' : `${t.delta > 0 ? '+' : ''}${n1(t.delta)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold text-ha-text">As Metas</h3>
          {n.metas.length === 0 ? <p className="text-xs text-ha-muted">Nenhuma meta definida no ciclo.</p> : null}
          <ul className="flex flex-col gap-3">
            {n.metas.map((m) => (
              <li key={m.tipo} className="text-sm">
                <p className="text-ha-text">
                  <span className="font-semibold">{m.nome}:</span> {m.descricao}
                </p>
                <p className="text-xs text-ha-muted">
                  De {valor(m.linha_base, m.unidade)} para {valor(m.alvo, m.unidade)}; chegou a {valor(m.atual, m.unidade)}
                  {m.atingida ? ' — atingida' : ''}
                </p>
                {m.progresso != null ? (
                  <div className="mt-1 h-1.5 w-full max-w-xs rounded bg-ha-border2">
                    <div className="h-1.5 rounded" style={{ width: `${Math.round(m.progresso * 100)}%`, background: 'var(--primary)' }} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold text-ha-text">Decisões, indicadores e manhãs</h3>
          <dl className="grid grid-cols-3 gap-2 text-center">
            {[
              ['Decisões', String(n.decisoes.total)],
              ['Cumprimento', n.decisoes.cumprimento == null ? '—' : `${n.decisoes.cumprimento}%`],
              ['No prazo', String(n.decisoes.no_prazo)],
              ['Manhãs', String(n.manhas.realizadas)],
              ['Nota dos sócios', n1(n.manhas.nota_media)],
              ['Atrasadas', String(n.decisoes.atrasadas)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-element border border-ha-border2 px-2 py-2">
                <dd className="text-lg font-bold text-ha-text">{v}</dd>
                <dt className="text-[11px] text-ha-muted">{k}</dt>
              </div>
            ))}
          </dl>
          {n.indicadores.length ? (
            <ul className="mt-3 flex flex-col gap-1 text-xs">
              {n.indicadores.map((i) => (
                <li key={i.nome} className="text-ha-text">
                  <span className="font-semibold">{i.nome}:</span> {valor(i.primeiro.valor, i.unidade)} ({mes(i.primeiro.competencia)}) para{' '}
                  {valor(i.ultimo.valor, i.unidade)} ({mes(i.ultimo.competencia)})
                  {i.dentro_da_faixa ? '' : <span className="text-red-700"> · fora da faixa</span>}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="mt-6 border-t border-ha-border2 pt-4">
        {b.pode_editar ? (
          <Parecer empresaId={empresaId} b={b} aoSalvar={() => void qc.invalidateQueries({ queryKey: ['conselheiro-balanco', empresaId] })} />
        ) : (
          <>
            <h3 className="mb-2 text-sm font-semibold text-ha-text">O parecer do conselheiro</h3>
            {b.parecer ? (
              <p className="whitespace-pre-wrap text-sm text-ha-text">{b.parecer}</p>
            ) : (
              <p className="text-xs text-ha-muted">O Conselheiro Principal ainda não escreveu o parecer.</p>
            )}
            {b.quem === 'CONSELHEIRO' && b.recomendacao ? (
              <p className="mt-3 text-xs text-ha-muted">
                Recomendação à gestão: <span className="font-semibold text-ha-text">{NOME_DA_RECOMENDACAO[b.recomendacao]}</span>
                {b.recomendacao_motivo ? ` — ${b.recomendacao_motivo}` : ''} (os sócios não veem)
              </p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function Parecer({ empresaId, b, aoSalvar }: { empresaId: string; b: Resposta; aoSalvar: () => void }) {
  const [parecer, setParecer] = useState(b.parecer ?? '');
  const [recomendacao, setRecomendacao] = useState<Recomendacao | null>(b.recomendacao ?? null);
  const [motivo, setMotivo] = useState(b.recomendacao_motivo ?? '');
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  useEffect(() => setMsg(null), [parecer, recomendacao, motivo]);

  const corpo = { parecer: parecer.trim() || null, recomendacao, recomendacao_motivo: motivo.trim() || null };
  const salvar = useMutation({
    mutationFn: async () => (await api.put(`/conselheiro/empresas/${empresaId}/balanco`, corpo)).data,
    onSuccess: () => {
      setMsg({ ok: true, texto: 'Rascunho salvo.' });
      aoSalvar();
    },
    onError: (e) => setMsg({ ok: false, texto: extractError(e).message }),
  });
  const publicar = useMutation({
    mutationFn: async () => {
      await api.put(`/conselheiro/empresas/${empresaId}/balanco`, corpo);
      return (await api.post(`/conselheiro/empresas/${empresaId}/balanco/publicar`)).data;
    },
    onSuccess: aoSalvar,
    onError: (e) => setMsg({ ok: false, texto: extractError(e).message }),
  });
  const falta = motivoParaNaoPublicar(corpo);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label htmlFor="parecer" className="text-sm font-semibold text-ha-text">
          O parecer aos sócios
        </label>
        <p className="text-xs text-ha-muted">
          O que o ano mudou, o que não andou e o que vem pela frente. É o que os sócios leem e levam no PDF.
        </p>
        <textarea
          id="parecer"
          rows={8}
          className="mt-1 w-full rounded-element border border-ha-border2 bg-ha-card p-2 text-sm"
          value={parecer}
          onChange={(e) => setParecer(e.target.value)}
        />
      </div>
      <fieldset>
        <legend className="text-sm font-semibold text-ha-text">A recomendação à gestão</legend>
        <p className="text-xs text-ha-muted">A gestão decide no HAOps. Os sócios não veem a recomendação.</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {RECOMENDACOES.map((r) => (
            <label
              key={r}
              className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${recomendacao === r ? 'border-black bg-black text-white' : 'border-ha-border2 text-ha-text'}`}
            >
              <input type="radio" name="recomendacao" className="sr-only" checked={recomendacao === r} onChange={() => setRecomendacao(r)} />
              {NOME_DA_RECOMENDACAO[r]}
            </label>
          ))}
        </div>
        <input
          className="mt-2 w-full rounded-element border border-ha-border2 bg-ha-card p-2 text-sm"
          placeholder="Por quê, em uma frase"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={2000}
        />
      </fieldset>
      {msg ? <p className={`text-xs ${msg.ok ? 'text-ha-muted' : 'text-red-600'}`}>{msg.texto}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-element border border-ha-border2 px-4 py-2 text-xs font-bold uppercase text-ha-text disabled:opacity-50"
          disabled={salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          Salvar rascunho
        </button>
        <button
          type="button"
          className={botao}
          style={{ background: 'var(--primary)' }}
          disabled={!!falta || publicar.isPending}
          title={falta ?? undefined}
          onClick={() => {
            if (window.confirm('Publicar o balanço? Os sócios recebem o e-mail e passam a ver. Depois não se edita mais.')) publicar.mutate();
          }}
        >
          Publicar aos sócios
        </button>
      </div>
      {falta ? <p className="text-xs text-ha-muted">Para publicar: {falta}</p> : null}
      <p className="text-[11px] text-ha-muted">O balanço abre no {MES_DO_BALANCO}º mês; os números se atualizam até a publicação, e aí congelam.</p>
    </div>
  );
}
